import { useCallback } from 'react';
import {
  DevHubChip,
  DevHubColorProps,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubLink,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { CGAAppItem } from '../types';
import { useCGAHierarchyStyles } from '../styles';

interface ApplicationsTableProps {
  applications: CGAAppItem[];
  selectedApps: CGAAppItem[];
  onSelectionChange: (apps: CGAAppItem[]) => void;
}

/**
 * Applications table component with selection, sorting, and pagination.
 * @param props - Component props
 * @param props.applications - Array of applications to display
 * @param props.selectedApps - Currently selected applications
 * @param props.onSelectionChange - Callback when selection changes
 */
export const ApplicationsTable = ({
  applications,
  selectedApps,
  onSelectionChange,
}: ApplicationsTableProps) => {
  const classes = useCGAHierarchyStyles();
  const baseUrl = window.location.origin;

  const ownerBodyTemplate = useCallback(
    (rowData: CGAAppItem) => rowData.owner?.displayName || 'Unknown',
    [],
  );

  const departmentBodyTemplate = useCallback(
    (rowData: CGAAppItem) => rowData.owner?.department || '-',
    [],
  );

  const serviceDomainBodyTemplate = useCallback(
    (rowData: CGAAppItem) => rowData.serviceDomain || '-',
    [],
  );

  const updatedBodyTemplate = useCallback((rowData: CGAAppItem) => {
    const date = rowData.updatedAt ? new Date(rowData.updatedAt) : null;
    return date ? date.toLocaleDateString() : 'Unavailable';
  }, []);

  const statusBodyTemplate = useCallback((rowData: CGAAppItem) => {
    const status = rowData.status || 'Unknown';
    const statusLower = status.toLowerCase();

    let chipColor: DevHubColorProps = 'default';
    if (statusLower === 'active') {
      chipColor = 'green';
    } else if (statusLower === 'pre-prod') {
      chipColor = 'blue';
    }

    return <DevHubChip label={status} chipColor={chipColor} chipSize="small" />;
  }, []);

  const resourcesBodyTemplate = useCallback(
    (rowData: CGAAppItem) => (
      <DevHubTooltip title="View application resources">
        <DevHubLink
          href={`${baseUrl}/catalog/default/system/${rowData.applicationId}/resources/cloud-accounts`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <DevHubIconButton size="medium">
            <DevHubDynamicIcon iconName="OpenInNew" fontSize="medium" />
          </DevHubIconButton>
        </DevHubLink>
      </DevHubTooltip>
    ),
    [baseUrl],
  );

  const handleRowClick = useCallback((e: DataTableRowClickEvent) => {
    if (
      !(e.originalEvent.target as Element).closest('button, a, .p-clickable')
    ) {
      const rowData = e.data as CGAAppItem;
      window.open(`/app/id/${rowData.applicationId}`, '_blank');
    }
  }, []);

  return (
    <DataTable
      value={applications}
      selection={selectedApps}
      onSelectionChange={e => onSelectionChange(e.value as CGAAppItem[])}
      dataKey="applicationId"
      selectionMode="checkbox"
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 25, 50, 100]}
      tableStyle={{ minWidth: '50rem', fontSize: '0.875rem' }}
      sortField="name"
      sortOrder={1}
      removableSort
      size="small"
      rowClassName={() => classes.clickableRow}
      onRowClick={handleRowClick}
    >
      <Column
        selectionMode="multiple"
        headerStyle={{ width: '3rem' }}
        exportable={false}
      />
      <Column
        field="applicationId"
        header="App ID"
        sortable
        style={{ width: '90px' }}
      />
      <Column field="name" header="Name" sortable style={{ width: '20%' }} />
      <Column
        field="owner.displayName"
        header="Owner"
        sortable
        body={ownerBodyTemplate}
        style={{ width: '15%' }}
      />
      <Column
        field="owner.department"
        header="Department"
        sortable
        body={departmentBodyTemplate}
        style={{ width: '15%' }}
      />
      <Column
        field="serviceDomain"
        header="Service Domain"
        sortable
        body={serviceDomainBodyTemplate}
        style={{ width: '15%' }}
      />
      <Column
        field="updatedAt"
        header="Updated"
        sortable
        style={{ width: '100px' }}
        body={updatedBodyTemplate}
      />
      <Column
        field="status"
        header="Status"
        sortable
        style={{ width: '100px', textAlign: 'center' }}
        body={statusBodyTemplate}
      />
      <Column
        header="Resources"
        body={resourcesBodyTemplate}
        style={{ width: '80px', textAlign: 'center' }}
        headerStyle={{ textAlign: 'center' }}
      />
    </DataTable>
  );
};
